import {Middleware} from '@reduxjs/toolkit'
import {parsePhoneNumber} from 'react-phone-number-input'
import AnalyticsUtils, {isGoodBot} from '../../utils'
import {StatusType} from '@datadog/browser-logs'
import {dataDogLogEvent} from '../../components/DataDogClient'
import {
  getUserId,
  getLoginCount,
  hasPlusTier,
  getUserTier,
  getCognitoHasPlusTier,
  getCognitoUserTier,
  getCognitoUserId,
  getCognitoLoginCount
} from '../../../auth/modules/selectors'
import {sanitizeCognitoRequestDetails} from './sanitizeCognitoRequestDetails'

import {
  makeOtpLogin,
  sendMessage,
  startLoginSignUp,
  parseUserTokenFromCallbackUrl,
  answerCustomChallenge,
  redirectUsersFromCallbackUrl
} from '../../../auth/modules/thunks'
import {
  userAuthenticationSuccess,
  trackPhoneNumberValidationError,
  trackUserSignInClick,
  trackUserAuthClick,
  trackSignUpClick,
  trackLoginClick
} from '../../../auth/modules/actions'
import {getDeviceId} from '../../../user/modules/device/slice'

import {trackEvent, TRACK_EXPERIMENT_SERVED} from '../actions'
import {getFullStoryObject} from '../../utils/FullStory'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Team
} from '../../types/Events'

/**
 * Fires an action to tracks that the user initiated a sign in process
 * @param metric - The event name
 * @param metadata - Extra event data
 * @param level - Type of the event from StatusType
 */
export const logEvent = (
  metric: string,
  metadata: any = undefined,
  level: StatusType = StatusType.info
) => {
  dataDogLogEvent(metric, metadata, level)
}

export const trackFullStoryEvent = (eventName, eventProperties) => {
  getFullStoryObject()?.event(eventName, eventProperties)
}

/**
 * Tracks all named event and pass in extra properties.
 * Assumes default level from logEvent
 * @param eventName - The event name
 * @param eventProperties - Extra event data
 */
export const trackAll = (eventName, eventProperties) => {
  trackFullStoryEvent(eventName, eventProperties)
  AnalyticsUtils.trackEvent(eventName, eventProperties)
  logEvent(eventName, eventProperties)
}

/**
 * Middleware to track all kinds of configured events
 */
const middleware: Middleware = store => next => async action => {
  const {dispatch} = store
  const state = store.getState()

  // TODO: The good bots are currently only filtered out for Search
  if (
    !AnalyticsUtils.analyticsPlatformEnabled() ||
    isGoodBot(state.meta?.userAgent)
  )
    return next(action)

  switch (action.type) {
    // TOOD: Write unit tests
    /** Track that a experiment was served to the user */
    case TRACK_EXPERIMENT_SERVED: {
      const {experimentId, variationId, isActive} = action
      console.log(
        `[OPTICKS] Experiment ${experimentId} is active on ${variationId} side`
      )
      AnalyticsUtils.trackEvent('ExperimentServed', {
        experimentId,
        variationId,
        isActive
      })
      break
    }

    /** Auth0 error handling */
    case makeOtpLogin.rejected.type:
    case parseUserTokenFromCallbackUrl.rejected.type: {
      const metadata = {
        errorType: 'MagicLinkLogin',
        errorMessage: action.payload,
        requestDetails: action.meta
      }

      const {selectedChannel, flow} = state?.authentication
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Authentication,
          action: Action.Errored,
          component: flow,
          analyticsContext: {
            [AnalyticsContext.ErrorContext]: metadata
          },
          payload: {
            authenticationMethod: selectedChannel
          },
          team: Team.Retention
        })
      )

      trackAll('ErrorTriggered', metadata)
      break
    }

    /* Cognito error handeling */
    case startLoginSignUp.rejected.type:
    case answerCustomChallenge.rejected.type: {
      const metadata = {
        errorType: 'MagicLinkLogin',
        errorMessage: action.payload,
        requestDetails: sanitizeCognitoRequestDetails(action.meta),
        ...(action.payload?.message
          ? {errorDetails: action.payload.message}
          : [])
      }

      const {selectedChannel, flow} = state?.authentication
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Authentication,
          action: Action.Errored,
          component: flow,
          analyticsContext: {
            [AnalyticsContext.ErrorContext]: metadata
          },
          payload: {
            authenticationMethod: selectedChannel
          },
          team: Team.Retention
        })
      )

      trackAll('ErrorTriggered', metadata)
      break
    }

    /** Log user authentication success */
    case userAuthenticationSuccess.type: {
      const {claims, sourceComponent} = action.payload
      const userId = getUserId(claims)
      const loginCount = getLoginCount(claims)
      const deviceId = getDeviceId(state)
      const memberPlus = hasPlusTier(getUserTier(claims))
      const {selectedChannel} = state?.authentication

      trackAll('UserAuthenticationSuccess', {
        userId,
        sourceComponent,
        loginCount
      })

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Authentication,
          action: Action.Succeeded,
          payload: {
            authenticationMethod: selectedChannel,
            userId,
            loginCount
          },
          team: Team.Retention
        })
      )

      AnalyticsUtils.identify(userId, {deviceId, memberPlus})
      break
    }

    /** Log pending authentication message */
    case startLoginSignUp.pending.type:
    case sendMessage.pending.type: {
      const {selectedChannel, phoneNumber} = action?.meta?.arg
      const {countryCallingCode: countryCallingPrefix} = parsePhoneNumber(
        phoneNumber
      )
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.MagicLinkSend,
          action: Action.Clicked,
          payload: {
            authenticationMethod: selectedChannel,
            countryCallingPrefix
          },
          team: Team.Retention
        })
      )

      const metadata = {
        action: 'Click',
        category: 'MagicLink',
        label: 'Send',
        meta: {channel: selectedChannel}
      }

      trackAll('UserAction', metadata)
      break
    }

    /** Log success authentication message */
    case startLoginSignUp.fulfilled.type:
    case sendMessage.fulfilled.type: {
      const {selectedChannel, phoneNumber} = action?.meta?.arg
      const {countryCallingCode: countryCallingPrefix} = parsePhoneNumber(
        phoneNumber
      )

      const metadata = {
        action: 'View',
        category: 'MagicLink',
        label: 'SendMagicLinkSuccess'
      }
      trackAll('UserAction', metadata)

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.MagicLinkSend,
          action: Action.Succeeded,
          payload: {
            authenticationMethod: selectedChannel,
            countryCallingPrefix
          },
          team: Team.Retention
        })
      )
      break
    }

    /** Log pending from click for authentication message */
    case makeOtpLogin.pending.type: {
      const {selectedChannel, phoneNumber} = action?.meta?.arg
      const {countryCallingCode: countryCallingPrefix} = parsePhoneNumber(
        phoneNumber
      )
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.MagicLinkCode,
          action: Action.Submitted,
          payload: {
            authenticationMethod: selectedChannel,
            countryCallingPrefix
          },
          team: Team.Retention
        })
      )

      const metadata = {
        action: 'Click',
        category: 'MagicLink',
        label: 'SubmitMagicLinkCode'
      }
      trackAll('UserAction', metadata)
      break
    }

    /** Log phone error from click for authentication message */
    case trackPhoneNumberValidationError.type: {
      const metadata = {
        errorType: 'PhoneNumberValidationError',
        errorMessage: action?.payload
      }
      trackAll('ErrorTriggered', metadata)

      const {selectedChannel, flow} = state?.authentication
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Authentication,
          action: Action.Errored,
          component: flow,
          analyticsContext: {
            [AnalyticsContext.ErrorContext]: metadata
          },
          payload: {
            authenticationMethod: selectedChannel
          },
          team: Team.Retention
        })
      )

      break
    }

    /**
     * Log user sign in click
     * @deprecated - use trackUserAuthClick instead
     */
    case trackUserSignInClick.type: {
      trackAll('UserAction', {
        type: 'click',
        element: action?.payload
      })
      break
    }

    /** Log user authentication click inside the overlay */
    case trackUserAuthClick.type: {
      const {authenticationMethod, sourceComponent} = action?.payload

      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.Authentication,
          action: Action.Clicked,
          component: sourceComponent,
          payload: {
            authenticationMethod
          },
          team: Team.Retention
        })
      )
      break
    }

    /** Log places where user open the auth overlay with a Signup button */
    case trackSignUpClick.type: {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.Signup,
          action: Action.Clicked,
          component: action?.payload,
          team: Team.Retention
        })
      )
      break
    }

    /** Log places where user open the auth overlay with a Login button */
    case trackLoginClick.type: {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.Login,
          action: Action.Clicked,
          component: action?.payload,
          team: Team.Retention
        })
      )
      break
    }

    case answerCustomChallenge.fulfilled.type:
    case redirectUsersFromCallbackUrl.fulfilled.type: {
      const user = action.payload
      const userId = getCognitoUserId(user)
      const loginCount = getCognitoLoginCount(user)
      const memberPlus = getCognitoHasPlusTier(getCognitoUserTier(user))
      const deviceId = getDeviceId(state)

      trackAll('UserAuthenticationSuccess', {
        userId,
        sourceComponent: 'MagicLink',
        loginCount
      })

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Authentication,
          action: Action.Succeeded,
          payload: {
            userId,
            loginCount
          },
          team: Team.Retention
        })
      )

      AnalyticsUtils.identify(userId, {deviceId, memberPlus})
      break
    }

    default:
      break
  }

  return next(action)
}

export default middleware
